// ===================================================
// appStorage.js — 앱 전용 데이터 저장 유틸
//
// 역할:
//   - localStorage를 직접 쓰는 대신 이 유틸을 통해 저장/읽기
//   - 네이티브 앱(iOS/Android)에서는 @capacitor/preferences도 함께 사용
//     → 네이티브 저장소(iOS NSUserDefaults, Android SharedPreferences)에 이중 저장
//     → iOS가 WebView 저장소를 비워도(저용량 시 자동 정리) 데이터 보존 가능
//   - 웹 브라우저에서는 기존 localStorage 그대로 사용
//
// 사용 방법:
//   import { appRead, appWrite, appRemove, restoreStorageFromNative } from './appStorage';
//
//   appWrite('my_key', 'my_value');          // 동기 (localStorage) + 비동기 (Preferences)
//   const val = appRead('my_key', '기본값'); // 동기 (localStorage에서 읽기)
//   await restoreStorageFromNative();        // 앱 시작 시 한 번 호출 → 복원
// ===================================================

import { Capacitor } from '@capacitor/core';

// 네이티브 앱 여부 (iOS 또는 Android)
const isNative = Capacitor.isNativePlatform();

// @capacitor/preferences 플러그인 (네이티브에서만 로드)
let Preferences = null;
if (isNative) {
  // 동적 import 대신 직접 require 스타일로 초기화
  // (앱 번들에 항상 포함되므로 안전)
  import('@capacitor/preferences').then(mod => {
    Preferences = mod.Preferences;
  });
}

// ── 기본 읽기/쓰기 함수 ────────────────────────────────────────

/**
 * 값 읽기 (동기, localStorage에서)
 * @param {string} key - 저장 키
 * @param {*} defaultValue - 저장된 값이 없을 때 반환할 기본값
 * @returns {string|null}
 */
export function appRead(key, defaultValue = null) {
  try {
    const val = localStorage.getItem(key);
    return val !== null ? val : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * 값 쓰기 (localStorage + 네이티브 Preferences)
 * localStorage는 동기로 즉시 저장, Preferences는 비동기 백그라운드로 저장
 * @param {string} key - 저장 키
 * @param {string} value - 저장할 값 (문자열로 변환됨)
 */
export function appWrite(key, value) {
  const strValue = String(value);
  try {
    localStorage.setItem(key, strValue);
  } catch {
    // 저장 공간 부족 등 예외 무시
  }
  // 네이티브 앱이고 플러그인이 로드됐으면 Preferences에도 저장
  if (isNative && Preferences) {
    Preferences.set({ key, value: strValue }).catch(() => {});
  }
}

/**
 * 값 삭제 (localStorage + 네이티브 Preferences)
 * @param {string} key - 삭제할 키
 */
export function appRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // 무시
  }
  if (isNative && Preferences) {
    Preferences.remove({ key }).catch(() => {});
  }
}

// ── 앱 시작 시 복원 함수 ──────────────────────────────────────

/**
 * Preferences → localStorage 복원
 *
 * iOS가 WKWebView 저장소(localStorage)를 초기화한 경우,
 * 네이티브 저장소(Preferences)에서 모든 키를 읽어 localStorage에 복원합니다.
 *
 * App.js의 최상위에서 딱 한 번 await해서 호출하세요.
 */
export async function restoreStorageFromNative() {
  // 웹 환경이면 아무것도 안 함
  if (!isNative) return;

  // Preferences 플러그인이 아직 로드 안 됐을 경우 대기
  if (!Preferences) {
    const mod = await import('@capacitor/preferences');
    Preferences = mod.Preferences;
  }

  try {
    const { keys } = await Preferences.keys();
    await Promise.all(
      keys.map(async key => {
        // localStorage에 이미 있으면 덮어쓰지 않음 (localStorage가 최신)
        if (localStorage.getItem(key) !== null) return;
        const { value } = await Preferences.get({ key });
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      })
    );
  } catch {
    // 복원 실패 시 조용히 무시 (앱은 정상 동작)
  }
}
